import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import useLaboratories from '@/hooks/useLaboratories';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Save, Upload, Brain, CheckCircle, AlertTriangle, Loader2, FileImage, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import StatusBadge from '@/components/StatusBadge';

const TEMPLATE_TEXT_LIMIT = 500;
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function MethodologyPage() {
  const labs = useLaboratories();
  const [selectedLab, setSelectedLab] = useState('teplota');
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [form, setForm] = useState({});
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const logoInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Seznam laboratoří z centrálního hooku (`useLaboratories`); první položka
  // 'nemereno' není laboratoř, drží se napevno jako default volba.
  const LABS = [
    { id: 'nemereno', label: 'Neměřeno' },
    ...labs.map((l) => ({ id: l.code, label: l.name })),
  ];

  const load = () => api.get(`/methodologies?laboratory=${selectedLab}`).then(r => setMethods(r.data)).catch(() => {});
  useEffect(() => { load(); }, [selectedLab]);

  const selectMethod = (m) => {
    setSelectedMethod(m.id);
    setForm({ ...m });
    setExtractedData(m.extractedData && Object.keys(m.extractedData).length > 0 ? m.extractedData : null);
    // Pre-fill logo preview pokud metodika logo má
    if (m.certificateTemplate?.logoPath) {
      fetchLogoPreview(m.id);
    } else {
      setLogoPreviewUrl('');
    }
  };

  // Preview loga: fetch s Bearer tokenem (`<img src="...">` token
  // nezvládne) → blob URL → useEffect uvolní při unmount/změně.
  const fetchLogoPreview = async (methodId) => {
    try {
      const token = localStorage.getItem('ct-token');
      const resp = await fetch(`${BACKEND_BASE}/api/methodologies/${methodId}/template-logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        setLogoPreviewUrl('');
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setLogoPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      setLogoPreviewUrl('');
    }
  };

  // Cleanup blob URL při unmount.
  useEffect(() => () => { if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link z kalibrace (?lab=<kód>&select=<methodId>) — nejprve přepni na
  // správnou laboratoř, ať se metodika objeví v seznamu.
  useEffect(() => {
    const lab = searchParams.get('lab');
    if (lab) setSelectedLab(lab);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Jakmile se metodiky pro danou laboratoř načtou, předvyber požadovanou
  // a spotřebuj parametry (ať se výběr neopakuje).
  useEffect(() => {
    const sel = searchParams.get('select');
    if (!sel || selectedMethod === sel) return;
    const m = methods.find((x) => x.id === sel);
    if (m) {
      selectMethod(m);
      searchParams.delete('select');
      searchParams.delete('lab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [methods]); // eslint-disable-line react-hooks/exhaustive-deps

  // Getter pro nested template fields s defaulty.
  const template = form.certificateTemplate || {};
  const templateHeader = template.headerText || '';
  const templateFooter = template.footerText || '';
  const hasLogo = !!template.logoPath;

  const updateTemplate = (patch) => {
    setForm({
      ...form,
      certificateTemplate: { ...template, ...patch },
    });
  };

  const triggerLogoUpload = () => logoInputRef.current?.click();

  const handleLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedMethod) return;
    if (!file.type.match(/^image\/(png|jpe?g|svg\+xml)$/)) {
      toast.error('Povoleno: PNG, JPG, SVG');
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error('Soubor je větší než 1 MB');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(
        `/methodologies/${selectedMethod}/template-logo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      updateTemplate({ logoPath: res.data.logoPath });
      fetchLogoPreview(selectedMethod);
      toast.success('Logo nahráno');
      load();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se nahrát logo');
    }
  };

  const removeLogo = async () => {
    if (!selectedMethod || !hasLogo) return;
    if (!window.confirm('Smazat logo metodiky?')) return;
    try {
      await api.delete(`/methodologies/${selectedMethod}/template-logo`);
      updateTemplate({ logoPath: '' });
      setLogoPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      toast.success('Logo smazáno');
      load();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se smazat logo');
    }
  };

  const create = async () => {
    try {
      const res = await api.post('/methodologies', { laboratory: selectedLab, code: 'KP nová', title: 'Nová metodika' });
      load();
      selectMethod(res.data);
      toast.success('Metodika vytvořena');
    } catch { toast.error('Chyba'); }
  };

  const save = async () => {
    if (!selectedMethod) return;
    try {
      await api.put(`/methodologies/${selectedMethod}`, form);
      load();
      toast.success('Uloženo');
    } catch { toast.error('Chyba'); }
  };

  const deleteMethod = async () => {
    if (!selectedMethod) return;
    if (!window.confirm('Opravdu smazat tuto metodiku? Akci nelze vrátit.')) return;
    try {
      await api.delete(`/methodologies/${selectedMethod}`);
      setSelectedMethod(null);
      setForm({});
      setExtractedData(null);
      setLogoPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
      load();
      toast.success('Metodika smazána');
    } catch (err) {
      toastApiError(err, 'Nepodařilo se smazat metodiku');
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/methodologies/${selectedMethod}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
      toast.success('PDF nahráno');
    } catch { toast.error('Chyba nahrávání'); }
  };

  const extractAI = async () => {
    if (!selectedMethod) return;
    setExtracting(true);
    try {
      const res = await api.post(`/methodologies/${selectedMethod}/extract`);
      if (res.data.extractedData) {
        setExtractedData(res.data.extractedData);
        toast.success(`Extrakce dokončena (confidence: ${(res.data.confidence * 100).toFixed(0)}%)`);
      } else if (res.data.error) {
        toast.error(res.data.error);
      }
    } catch (err) {
      toastApiError(err, 'Chyba AI extrakce');
    } finally {
      setExtracting(false);
    }
  };

  const verifyData = async () => {
    if (!selectedMethod || !extractedData) return;
    try {
      await api.put(`/methodologies/${selectedMethod}/verify`, { extractedData });
      load();
      toast.success('Metodika ověřena a uložena');
    } catch { toast.error('Chyba'); }
  };

  // Editace předepsaných (jmenovitých) podmínek prostředí — norma/metodika.
  // Ukládá se přes verifyData (extractedData.environmentalConditions).
  const updateEnvCond = (kind, bound, unit) => (e) => {
    const raw = e.target.value;
    setExtractedData((prev) => {
      const ec = { ...(prev?.environmentalConditions || {}) };
      const cur = { unit, ...(ec[kind] || {}) };
      cur[bound] = raw === '' ? null : parseFloat(raw);
      ec[kind] = cur;
      return { ...(prev || {}), environmentalConditions: ec };
    });
  };
  // Editace dovolené chyby (MPE) pro posouzení shody — ukládá se do
  // extractedData.toleranceCriteria. Použije se na kalibračním listu (sloupec
  // MPE + per-bod shoda) a při deklaraci rozhodovacího pravidla.
  const updateTolerance = (field) => (e) => {
    const raw = e.target.value;
    setExtractedData((prev) => {
      const tc = { ...(prev?.toleranceCriteria || {}) };
      if (field === 'value' || field === 'fsValue') tc[field] = raw === '' ? null : parseFloat(raw);
      else tc[field] = raw;
      return { ...(prev || {}), toleranceCriteria: tc };
    });
  };
  const renderToleranceCriteria = () => {
    const tc = extractedData?.toleranceCriteria || {};
    const mode = tc.mode || 'abs';
    const inputStyle = { background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' };
    return (
      <div className="ct-form-field col-span-2">
        <label className="ct-form-label">Dovolená chyba (MPE) — pro posouzení shody</label>
        <div className="flex flex-wrap items-center gap-2 text-sm p-2 rounded" style={inputStyle}>
          <select value={mode} onChange={updateTolerance('mode')}
            className="h-8 text-sm rounded px-2" style={inputStyle}>
            <option value="abs">absolutní (±)</option>
            <option value="pctReading">% z měřené hodnoty</option>
            <option value="pctFS">% z rozsahu (FS)</option>
          </select>
          <input type="number" value={tc.value ?? ''} onChange={updateTolerance('value')} placeholder="hodnota"
            className="h-8 w-24 text-sm rounded px-2" style={inputStyle} />
          {mode === 'pctFS' && (
            <input type="number" value={tc.fsValue ?? ''} onChange={updateTolerance('fsValue')} placeholder="rozsah (FS)"
              className="h-8 w-28 text-sm rounded px-2" style={inputStyle} />
          )}
          <input type="text" value={tc.description ?? ''} onChange={updateTolerance('description')} placeholder="popis (volitelné)"
            className="h-8 flex-1 min-w-[140px] text-sm rounded px-2" style={inputStyle} />
        </div>
        <span className="text-xs mt-1" style={{ color: 'var(--ct-text-secondary)' }}>
          Prázdná hodnota = shoda se na listu nevyhodnocuje (uvede se jen rozhodovací pravidlo).
        </span>
      </div>
    );
  };
  const renderEnvConditions = () => {
    const ec = extractedData?.environmentalConditions || {};
    const t0 = ec.temperature || {};
    const h0 = ec.humidity || {};
    const cell = (val, onChange, ph) => (
      <input type="number" value={val ?? ''} onChange={onChange} placeholder={ph}
        className="h-8 w-20 text-sm rounded px-2"
        style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }} />
    );
    return (
      <div className="ct-form-field col-span-2">
        <label className="ct-form-label">Předepsané podmínky prostředí (norma / metodika)</label>
        <div className="flex flex-wrap items-center gap-3 text-sm p-2 rounded"
          style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
          <span style={{ color: 'var(--ct-text-secondary)' }}>Teplota (°C):</span>
          {cell(t0.min, updateEnvCond('temperature', 'min', '°C'), 'min')}
          <span>–</span>
          {cell(t0.max, updateEnvCond('temperature', 'max', '°C'), 'max')}
          <span className="ml-3" style={{ color: 'var(--ct-text-secondary)' }}>Vlhkost (%RV):</span>
          {cell(h0.min, updateEnvCond('humidity', 'min', '% r.v.'), 'min')}
          <span>–</span>
          {cell(h0.max, updateEnvCond('humidity', 'max', '% r.v.'), 'max')}
        </div>
      </div>
    );
  };

  const renderConfidenceField = (value, label) => {
    const confidence = extractedData?.confidence?.overall || 1;
    const lowFields = extractedData?.confidence?.lowConfidenceFields || [];
    const isLow = lowFields.some(f => label.toLowerCase().includes(f.toLowerCase())) || confidence < 0.8;
    return (
      <div className={`ct-form-field ${isLow ? 'ring-1 ring-yellow-500/50 rounded-md p-2' : ''}`}>
        <label className="ct-form-label flex items-center gap-1">
          {label}
          {isLow && <AlertTriangle className="w-3 h-3" style={{ color: 'var(--ct-warning)' }} />}
        </label>
        <div className="text-sm p-2 rounded break-words" style={{ background: 'var(--ct-bg)', border: `1px solid ${isLow ? 'var(--ct-warning)' : 'var(--ct-border)'}` }}>
          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '—')}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="methodology-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Col 1: Labs */}
      <div className="ct-panel w-52 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header">Laboratoře</div>
        <ScrollArea className="flex-1">
          {LABS.map(l => (
            <button
              key={l.id}
              onClick={() => { setSelectedLab(l.id); setSelectedMethod(null); }}
              className={`ct-table-row w-full text-left px-3 py-2 text-sm ${selectedLab === l.id ? 'active' : ''}`}
            >
              {l.label}
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Col 2: Methods */}
      <div className="ct-panel w-72 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center justify-between">
          <span>Metodiky</span>
          <button data-testid="add-methodology-btn" onClick={create} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <ScrollArea className="flex-1">
          {methods.map(m => (
            <button
              key={m.id}
              onClick={() => selectMethod(m)}
              className={`ct-table-row w-full text-left px-3 py-2 text-sm ${selectedMethod === m.id ? 'active' : ''}`}
            >
              <div className="flex items-center gap-2">
                {m.verified && <CheckCircle className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--ct-success)' }} />}
                <div>
                  <div className="font-mono">{m.code}</div>
                  <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{m.title}</div>
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Col 3: Detail */}
      {selectedMethod ? (
        <div className="flex-1 overflow-auto space-y-4">
          <div className="ct-panel">
            <div className="ct-panel-header">Detail metodiky</div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="ct-form-field">
                <label className="ct-form-label">Kód</label>
                <Input value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} className="h-9 text-sm font-mono" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Verze</label>
                <Input value={form.version || ''} onChange={e => setForm({ ...form, version: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">Název</label>
                <Input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Prefix číselné řady</label>
                <Input value={form.numberSeriesPrefix || ''} onChange={e => setForm({ ...form, numberSeriesPrefix: e.target.value })} className="h-9 text-sm font-mono" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Další číslo</label>
                <Input type="number" value={form.numberSeriesNextNumber ?? 1} onChange={e => setForm({ ...form, numberSeriesNextNumber: parseInt(e.target.value) || 1 })} className="h-9 text-sm font-mono" />
              </div>
            </div>
            <div className="px-4 pb-4 flex gap-2">
              <Button onClick={save} className="ct-btn-primary gap-1">
                <Save className="w-4 h-4" /> Uložit
              </Button>
              <Button
                data-testid="delete-methodology-btn"
                onClick={deleteMethod}
                className="ct-btn-secondary gap-1"
                style={{ color: 'var(--ct-error)' }}
              >
                <Trash2 className="w-4 h-4" /> Smazat metodiku
              </Button>
            </div>
          </div>

          {/* PDF Upload + AI */}
          <div className="ct-panel">
            <div className="ct-panel-header flex items-center gap-2">
              <Brain className="w-4 h-4" />
              AI Extrakce metodiky
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <label className="ct-btn-secondary flex items-center gap-1 cursor-pointer">
                  <Upload className="w-4 h-4" /> Nahrát metodiku (PDF)
                  <input type="file" accept=".pdf" onChange={uploadPdf} className="hidden" />
                </label>
                <Button
                  data-testid="extract-ai-btn"
                  onClick={extractAI}
                  disabled={extracting || !form.uploadedPdfPath}
                  className="ct-btn-primary gap-1"
                >
                  {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                  {extracting ? 'Extrahování...' : 'Spustit AI extrakci'}
                </Button>
              </div>
              {form.uploadedPdfPath && (
                <div className="text-xs" style={{ color: 'var(--ct-success)' }}>PDF nahráno</div>
              )}

              {extractedData && (
                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--ct-accent)' }}>Extrahovaná data</h3>
                    <StatusBadge tone="info">
                      Confidence: {((extractedData.confidence?.overall || 0) * 100).toFixed(0)}%
                    </StatusBadge>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {renderConfidenceField(extractedData.metadata?.code, 'Kód')}
                    {renderConfidenceField(extractedData.metadata?.title, 'Název')}
                    {renderConfidenceField(extractedData.metadata?.quantity, 'Veličina')}
                    {renderConfidenceField(extractedData.metadata?.applicableTo, 'Aplikovatelné na')}
                    {renderConfidenceField(extractedData.calibrationPoints?.points, 'Kalibrační body')}
                    {renderConfidenceField(extractedData.series?.count, 'Počet sérií')}
                    {renderEnvConditions()}
                    {renderToleranceCriteria()}
                    {renderConfidenceField(extractedData.uncertaintyCalculation, 'Výpočet nejistoty')}
                  </div>
                  {extractedData.calibrationProcedure && (
                    <div className="ct-form-field">
                      <label className="ct-form-label">Postup kalibrace</label>
                      <div className="p-2 rounded text-sm space-y-1" style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
                        {extractedData.calibrationProcedure.map((step, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{i + 1}.</span>
                            <span>{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button data-testid="verify-methodology-btn" onClick={verifyData} className="ct-btn-primary gap-1">
                    <CheckCircle className="w-4 h-4" /> Uložit metodiku
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Šablona kalibračního listu (logo + texty) */}
          <div className="ct-panel">
            <div className="ct-panel-header flex items-center gap-2">
              <FileImage className="w-4 h-4" />
              Šablona kalibračního listu
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                Logo a texty se vykreslí v hlavičce/patičce vygenerovaného PDF
                kalibračního listu (per-sonda i Master KL). Layout (tabulky,
                fonty) zůstává jednotný — ISO 17025 vyžaduje konzistentní strukturu.
              </p>

              {/* Logo upload */}
              <div>
                <label className="ct-form-label">Logo laboratoře</label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoSelected}
                  className="hidden"
                />
                <div className="flex items-center gap-3 mt-1">
                  {hasLogo && logoPreviewUrl ? (
                    <div
                      className="rounded border p-2 flex items-center justify-center"
                      style={{ background: '#fff', borderColor: 'var(--ct-border)', width: 100, height: 60 }}
                    >
                      <img
                        src={logoPreviewUrl}
                        alt="Náhled loga"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  ) : (
                    <div
                      className="rounded border-2 border-dashed flex items-center justify-center"
                      style={{ borderColor: 'var(--ct-border)', width: 100, height: 60, color: 'var(--ct-text-disabled)' }}
                    >
                      <FileImage className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <Button
                      onClick={triggerLogoUpload}
                      size="sm"
                      className="ct-btn-secondary gap-1 text-xs h-7"
                    >
                      <Upload className="w-3 h-3" /> {hasLogo ? 'Nahradit' : 'Nahrát logo'}
                    </Button>
                    {hasLogo && (
                      <Button
                        onClick={removeLogo}
                        size="sm"
                        className="ct-btn-secondary gap-1 text-xs h-7"
                        style={{ color: 'var(--ct-error)' }}
                      >
                        <Trash2 className="w-3 h-3" /> Smazat
                      </Button>
                    )}
                  </div>
                  <p className="text-xs ml-2" style={{ color: 'var(--ct-text-disabled)' }}>
                    PNG, JPG, SVG · max 1 MB · doporučený poměr ~5:3 (např. 500×300 px).
                  </p>
                </div>
              </div>

              {/* Header text */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="ct-form-label">Hlavička (text pod logem)</label>
                  <span className="text-xs" style={{ color: templateHeader.length > TEMPLATE_TEXT_LIMIT ? 'var(--ct-error)' : 'var(--ct-text-disabled)' }}>
                    {templateHeader.length} / {TEMPLATE_TEXT_LIMIT}
                  </span>
                </div>
                <Textarea
                  value={templateHeader}
                  onChange={e => updateTemplate({ headerText: e.target.value.slice(0, TEMPLATE_TEXT_LIMIT) })}
                  placeholder='Např. "Akreditovaná kalibrační laboratoř č. ČIA 0123" - zobrazí se na každé stránce KL pod logem.'
                  rows={2}
                  className="text-sm"
                />
              </div>

              {/* Footer text */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="ct-form-label">Patička (text vlevo dole)</label>
                  <span className="text-xs" style={{ color: templateFooter.length > TEMPLATE_TEXT_LIMIT ? 'var(--ct-error)' : 'var(--ct-text-disabled)' }}>
                    {templateFooter.length} / {TEMPLATE_TEXT_LIMIT}
                  </span>
                </div>
                <Textarea
                  value={templateFooter}
                  onChange={e => updateTemplate({ footerText: e.target.value.slice(0, TEMPLATE_TEXT_LIMIT) })}
                  placeholder="Např. právní disclaimer, kontaktní info, IBAN, DIČ - zobrazí se na každé stránce v patičce."
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'var(--ct-border)' }}>
                <Button onClick={save} className="ct-btn-primary gap-1">
                  <Save className="w-4 h-4" /> Uložit šablonu
                </Button>
                <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                  Logo se ukládá ihned po nahrání. Texty se uloží tlačítkem.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--ct-text-disabled)' }}>
          Vyberte metodiku
        </div>
      )}
    </div>
  );
}
